import React, { useState, useEffect } from 'react';
import { Col, Form, Row, Button, Card } from 'react-bootstrap';
import PropTypes from 'prop-types';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import axios from 'helpers/api';
import { toast } from 'react-toastify';
import { createIframeReportBlobUrl } from 'helpers/iframeReportPreview';
import Spinner from 'react-bootstrap/Spinner';
import ThemedSelect from 'components/commonbreadcrumb/ThemedSelect';

const UsersLoginActivityReport = (props) => {
    const [spinner, showSpinner] = useState(false);
    const [iframeSrc, setIframeSrc] = useState(null);

    useEffect(() => {
        if (props.setFlag) {
            props.setFlag(true);
        }
    }, [props.setFlag]);

    const [formData, setFormData] = useState({
        start_date: null,
        end_date: null,
        no_of_days: '',
        login_source: '',
        login_status: ''
    });

    const loginSourceOptions = [
        { value: '', label: 'All' },
        { value: 1, label: 'Web' },
        { value: 2, label: 'Mobile' },
        { value: 3, label: 'iOS' }
    ];

    const loginStatusOptions = [
        { value: '', label: 'All' },
        { value: 'success', label: 'Success' },
        { value: 'failed', label: 'Failed' },
        { value: 'mfa_pending', label: 'MFA Pending' }
    ];

    const handleDownloadReport = async (e) => {
        e.preventDefault();
        showSpinner(true);

        let tokenStr = localStorage.getItem("accessToken");
        let url = `${process.env.REACT_APP_SERVER_URL}/reports/users-login-activity-report`;

        let payload = {
            group_id: localStorage.getItem("groupId"),
            is_print: true
        };

        if (formData.start_date) payload.start_date = moment(formData.start_date).format('YYYY-MM-DD');
        if (formData.end_date) payload.end_date = moment(formData.end_date).format('YYYY-MM-DD');
        if (formData.no_of_days) payload.no_of_days = parseInt(formData.no_of_days, 10);
        if (formData.login_source) payload.login_source = parseInt(formData.login_source, 10);
        if (formData.login_status) payload.login_status = formData.login_status;

        try {
            const { data } = await axios.post(url, payload, { headers: { 'Authorization': `Bearer ${tokenStr}` } });

            if (data.status === false) {
                toast.error(data.message || "Failed to generate report");
            } else {
                toast.success("Report Generated Successfully");
                const { blobUrl } = await createIframeReportBlobUrl(data);
                setIframeSrc(blobUrl);
            }
        } catch (error) {
            toast.error("Error generating report");
        } finally {
            showSpinner(false);
        }
    };

    const handleDownloadCSV = async (e) => {
        e.preventDefault();
        showSpinner(true);

        let tokenStr = localStorage.getItem("accessToken");
        let url = `${process.env.REACT_APP_SERVER_URL}/reports/users-login-activity-report`;

        let payload = {
            group_id: props.selectedFacility || localStorage.getItem("reportFacilityId"),
            is_print: "csv"
        };

        if (formData.start_date) payload.start_date = moment(formData.start_date).format('YYYY-MM-DD');
        if (formData.end_date) payload.end_date = moment(formData.end_date).format('YYYY-MM-DD');
        if (formData.no_of_days) payload.no_of_days = parseInt(formData.no_of_days, 10);
        if (formData.login_source) payload.login_source = parseInt(formData.login_source, 10);
        if (formData.login_status) payload.login_status = formData.login_status;

        try {
            const data = await axios.post(url, payload, { headers: { 'Authorization': `Bearer ${tokenStr}` }, responseType: 'blob' });

            const disposition = data.headers['content-disposition'];
            let filename = 'users-login-activity-report.csv';
            if (disposition && disposition.indexOf('filename=') !== -1) {
                filename = disposition.split('filename=')[1]?.replace(/"/g, '').trim() || filename;
            }

            const blobUrl = URL.createObjectURL(new Blob([data.data]));
            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            URL.revokeObjectURL(blobUrl);

            toast.success("CSV Exported Successfully");
        } catch (error) {
            toast.error("Error exporting CSV");
        } finally {
            showSpinner(false);
        }
    };

    const isDownloadDisabled = spinner;

    return (
        <div className="mt-4">
            <div className="mb-3">
                <div className="p-4">
                    <Row>
                        <Col md={4} className="mb-3">
                            <Form.Group>
                                <Form.Label className="fw-semibold text-700">Start Date</Form.Label>
                                <DatePicker
                                    selected={formData.start_date}
                                    onChange={(date) => setFormData({ ...formData, start_date: date })}
                                    className="form-control"
                                    placeholderText="Select Start Date"
                                    dateFormat="MM/dd/yyyy"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4} className="mb-3">
                            <Form.Group>
                                <Form.Label className="fw-semibold text-700">End Date</Form.Label>
                                <DatePicker
                                    selected={formData.end_date}
                                    onChange={(date) => setFormData({ ...formData, end_date: date })}
                                    className="form-control"
                                    placeholderText="Select End Date"
                                    dateFormat="MM/dd/yyyy"
                                    minDate={formData.start_date}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4} className="mb-3">
                            <Form.Group>
                                <Form.Label className="fw-semibold text-700">Look-back Window (Days)</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={formData.no_of_days}
                                    onChange={(e) => setFormData({ ...formData, no_of_days: e.target.value })}
                                    placeholder="e.g. 30"
                                    min="1"
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6} className="mb-3">
                            <Form.Group>
                                <Form.Label className="fw-semibold text-700">Login Source</Form.Label>
                                <ThemedSelect
                                    name="login_source"
                                    classNamePrefix="react-select"
                                    options={loginSourceOptions}
                                    value={loginSourceOptions.find(opt => opt.value === formData.login_source) || loginSourceOptions[0]}
                                    getOptionLabel={option => option.label}
                                    getOptionValue={option => option.value}
                                    onChange={(selected) => setFormData({ ...formData, login_source: selected.value })}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6} className="mb-3">
                            <Form.Group>
                                <Form.Label className="fw-semibold text-700">Login Status</Form.Label>
                                <ThemedSelect
                                    name="login_status"
                                    classNamePrefix="react-select"
                                    options={loginStatusOptions}
                                    value={loginStatusOptions.find(opt => opt.value === formData.login_status) || loginStatusOptions[0]}
                                    getOptionLabel={option => option.label}
                                    getOptionValue={option => option.value}
                                    onChange={(selected) => setFormData({ ...formData, login_status: selected.value })}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </div>
            </div>

            <Form>
                <Row>
                    <Col xs={6} className="text-center">
                        {spinner ? (
                            <Button variant="primary" disabled className="mt-3 w-75 px-4 py-2">
                                <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />
                                <span className="ms-2">Generating...</span>
                            </Button>
                        ) : (
                            <Button disabled={isDownloadDisabled} onClick={handleDownloadReport} variant="primary" className="mt-3 w-75 px-4 py-2">
                                Download Report
                            </Button>
                        )}
                    </Col>
                    <Col xs={6} className="text-center">
                        {spinner ? (
                            <Button variant="primary" disabled className="mt-3 w-75 px-4 py-2">
                                <Spinner as="span" animation="grow" size="sm" role="status" aria-hidden="true" />
                                <span className="ms-2">Exporting...</span>
                            </Button>
                        ) : (
                            <Button disabled={isDownloadDisabled} onClick={handleDownloadCSV} variant="primary" className="mt-3 w-75 px-4 py-2">
                                Export CSV
                            </Button>
                        )}
                    </Col>
                </Row>
                <Row className="mt-3">
                    <Col>
                        <div style={{ height: 0, overflow: 'hidden' }}>
                            {iframeSrc && (
                                <iframe
                                    src={iframeSrc}
                                    style={{ width: "100%", height: "400px", border: "none", borderRadius: "8px" }}
                                    title="Preview Content"
                                />
                            )}
                        </div>
                    </Col>
                </Row>
                <Row className="mt-2">
                    <Col xs={12} className="text-end">
                        <Form.Label><span className="text-danger">*</span> indicates required field</Form.Label>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};

UsersLoginActivityReport.propTypes = {
    selectedFacility: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    validateFacility: PropTypes.func.isRequired,
    setFlag: PropTypes.func
};

export default UsersLoginActivityReport;
